import {
  AGENT_OUTCOME_ENABLED_VALUE,
  AGENT_OUTCOME_REF_KEY,
  AGENT_OUTCOME_RESOURCE_SCHEMA_REF_KEY,
  type StoredToolSchemaMappingRefs,
  claimsToolAgentOutcome,
  isAgentOutcomeResourceSchemaCategory,
  readToolAgentOutcomeConfig,
  requiresAgentOutcomeResourceIdMapping,
  withToolAgentOutcome,
  withoutToolAgentOutcome,
} from '@reach/shared-types/tool-agent-outcome';
import type { ToolResponseProjection } from '@reach/shared-types/tool-response-projection';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { partnerApi } from '../../lib/api';
import { schemaDefinitionKeys } from '../../lib/schema-definition-query-keys';
import outcomeStyles from './AgentOutcome.module.css';
import { AGENT_OUTCOME_COPY, unreadableAgentOutcomeMessage } from './agent-outcome-copy';
import { ToolFormField as Field } from './ToolFormField';
import { ToolFormSection as Section } from './ToolFormSection';
import formStyles from './ToolForm.module.css';

const SCHEMA_DEFINITIONS_STALE_TIME_MS = 5 * 60 * 1000;
const SCHEMA_DEFINITIONS_GC_TIME_MS = 10 * 60 * 1000;

interface AgentOutcomeRefsSnapshot {
  marker: string | undefined;
  resourceSchemaDefinitionId: string | undefined;
}

const snapshotAgentOutcomeRefs = (
  schemaMappingRefs: StoredToolSchemaMappingRefs | null,
): AgentOutcomeRefsSnapshot => ({
  marker: schemaMappingRefs?.[AGENT_OUTCOME_REF_KEY],
  resourceSchemaDefinitionId: schemaMappingRefs?.[AGENT_OUTCOME_RESOURCE_SCHEMA_REF_KEY],
});

const agentOutcomeRefsMatch = (
  first: AgentOutcomeRefsSnapshot,
  second: AgentOutcomeRefsSnapshot,
): boolean =>
  first.marker === second.marker &&
  first.resourceSchemaDefinitionId === second.resourceSchemaDefinitionId;

const selectEligibleAgentOutcomeSchemas = (
  schemas: Awaited<ReturnType<typeof partnerApi.getSchemaDefinitions>>,
) =>
  schemas.filter(
    schema => !schema.deactivated_at && isAgentOutcomeResourceSchemaCategory(schema.category),
  );

interface AgentOutcomeSectionProps {
  step: number;
  schemaMappingRefs: StoredToolSchemaMappingRefs | null;
  responseMapping: ToolResponseProjection | null;
  onChange: (schemaMappingRefs: StoredToolSchemaMappingRefs | null) => void;
  onValidityChange: (isValid: boolean) => void;
}

export const AgentOutcomeSection = ({
  step,
  schemaMappingRefs,
  responseMapping,
  onChange,
  onValidityChange,
}: AgentOutcomeSectionProps) => {
  const config = readToolAgentOutcomeConfig(schemaMappingRefs);
  const declaredMarker = schemaMappingRefs?.[AGENT_OUTCOME_REF_KEY];
  const selectedSchemaId = schemaMappingRefs?.[AGENT_OUTCOME_RESOURCE_SCHEMA_REF_KEY] ?? '';
  const outcomeIsEnabled = config !== null;
  const resourceSchemaKeyIsPresent = Object.prototype.hasOwnProperty.call(
    schemaMappingRefs ?? {},
    AGENT_OUTCOME_RESOURCE_SCHEMA_REF_KEY,
  );
  const [resourceLinkingIsEnabled, setResourceLinkingIsEnabled] = useState(resourceSchemaKeyIsPresent);
  const emittedRefs = useRef(snapshotAgentOutcomeRefs(schemaMappingRefs));

  useEffect(() => {
    const incomingRefs = snapshotAgentOutcomeRefs(schemaMappingRefs);
    if (agentOutcomeRefsMatch(incomingRefs, emittedRefs.current)) return;
    emittedRefs.current = incomingRefs;
    setResourceLinkingIsEnabled(resourceSchemaKeyIsPresent);
  }, [resourceSchemaKeyIsPresent, schemaMappingRefs]);

  const emitRefs = (refs: StoredToolSchemaMappingRefs | null) => {
    emittedRefs.current = snapshotAgentOutcomeRefs(refs);
    onChange(refs);
  };

  const schemaDefinitionsQuery = useQuery({
    queryKey: schemaDefinitionKeys.list(),
    queryFn: () => partnerApi.getSchemaDefinitions(),
    select: selectEligibleAgentOutcomeSchemas,
    enabled: outcomeIsEnabled && resourceLinkingIsEnabled,
    staleTime: SCHEMA_DEFINITIONS_STALE_TIME_MS,
    gcTime: SCHEMA_DEFINITIONS_GC_TIME_MS,
  });
  const { data: eligibleSchemas } = schemaDefinitionsQuery;
  const selectedSchemaIsAvailable =
    selectedSchemaId === '' || eligibleSchemas?.some(schema => schema.id === selectedSchemaId) === true;
  const schemasAreLoading =
    resourceLinkingIsEnabled && eligibleSchemas === undefined && schemaDefinitionsQuery.isFetching;
  const schemasFailedToLoad =
    resourceLinkingIsEnabled && schemaDefinitionsQuery.isError && !schemaDefinitionsQuery.isFetching;

  const setOutcomeEnabled = (enabled: boolean) => {
    if (!enabled) {
      const remainingRefs = withoutToolAgentOutcome(schemaMappingRefs);
      setResourceLinkingIsEnabled(false);
      emitRefs(Object.keys(remainingRefs).length > 0 ? remainingRefs : null);
      return;
    }
    emitRefs(
      withToolAgentOutcome({
        schemaMappingRefs,
        resourceSchemaDefinitionId: selectedSchemaId || undefined,
      }),
    );
  };

  const setResourceLinkingEnabled = (enabled: boolean) => {
    if (!outcomeIsEnabled) return;
    setResourceLinkingIsEnabled(enabled);
    if (!enabled) {
      emitRefs(withToolAgentOutcome({ schemaMappingRefs }));
      return;
    }
    emitRefs(withToolAgentOutcome({ schemaMappingRefs, resourceSchemaDefinitionId: selectedSchemaId }));
  };

  const setSchemaId = (schemaDefinitionId: string) => {
    if (!resourceLinkingIsEnabled) return;
    emitRefs(
      withToolAgentOutcome({
        schemaMappingRefs,
        resourceSchemaDefinitionId: schemaDefinitionId,
      }),
    );
  };

  const configurationStarted = claimsToolAgentOutcome(schemaMappingRefs);
  const markerIsUnreadable = configurationStarted && declaredMarker !== AGENT_OUTCOME_ENABLED_VALUE;
  const linkedOutcomeIsEnabled = outcomeIsEnabled && resourceLinkingIsEnabled;
  const schemaIsMissing = linkedOutcomeIsEnabled && selectedSchemaId === '';
  const schemaIsUnavailable =
    eligibleSchemas !== undefined && selectedSchemaId !== '' && !selectedSchemaIsAvailable;
  const resourceIdIsMissing = requiresAgentOutcomeResourceIdMapping(schemaMappingRefs, responseMapping);
  const requiredSchemasAreLoaded =
    !linkedOutcomeIsEnabled || (eligibleSchemas !== undefined && !schemaDefinitionsQuery.isError);
  const configurationIsComplete =
    !markerIsUnreadable &&
    !schemaIsMissing &&
    !schemaIsUnavailable &&
    !resourceIdIsMissing &&
    requiredSchemasAreLoaded;

  useEffect(() => {
    onValidityChange(configurationIsComplete);
  }, [configurationIsComplete, onValidityChange]);

  return (
    <Section
      step={step}
      title={AGENT_OUTCOME_COPY.sectionTitle}
      subtitle={AGENT_OUTCOME_COPY.sectionSubtitle}>
      <fieldset className={outcomeStyles.controls}>
        <legend>{AGENT_OUTCOME_COPY.controlsLegend}</legend>
        <label className={outcomeStyles.control}>
          <input
            type="checkbox"
            aria-label={AGENT_OUTCOME_COPY.enableLabel}
            checked={outcomeIsEnabled}
            onChange={event => setOutcomeEnabled(event.target.checked)}
          />
          <span>
            <strong>{AGENT_OUTCOME_COPY.enableLabel}</strong>
            <small>{AGENT_OUTCOME_COPY.enableDescription}</small>
          </span>
        </label>
        {outcomeIsEnabled && (
          <label className={outcomeStyles.control}>
            <input
              type="checkbox"
              aria-label={AGENT_OUTCOME_COPY.linkResourceLabel}
              checked={resourceLinkingIsEnabled}
              onChange={event => setResourceLinkingEnabled(event.target.checked)}
            />
            <span>
              <strong>{AGENT_OUTCOME_COPY.linkResourceLabel}</strong>
              <small>{AGENT_OUTCOME_COPY.linkResourceDescription}</small>
            </span>
          </label>
        )}
      </fieldset>

      {markerIsUnreadable && (
        <span className={formStyles.fieldError} role="alert">
          {unreadableAgentOutcomeMessage(declaredMarker)}
        </span>
      )}

      {linkedOutcomeIsEnabled && (
        <>
          <Field label={AGENT_OUTCOME_COPY.schemaLabel} hint={AGENT_OUTCOME_COPY.schemaHint}>
            <select
              value={selectedSchemaId}
              onChange={event => setSchemaId(event.target.value)}
              className={formStyles.select}
              disabled={eligibleSchemas === undefined || schemaDefinitionsQuery.isError}
              aria-label={AGENT_OUTCOME_COPY.schemaLabel}>
              <option value="">{AGENT_OUTCOME_COPY.chooseSchema}</option>
              {selectedSchemaId !== '' && eligibleSchemas === undefined && (
                <option value={selectedSchemaId}>{AGENT_OUTCOME_COPY.savedSchema}</option>
              )}
              {schemaIsUnavailable && (
                <option value={selectedSchemaId} disabled>
                  {AGENT_OUTCOME_COPY.savedSchemaUnavailable}
                </option>
              )}
              {(eligibleSchemas ?? []).map(schema => (
                <option key={schema.id} value={schema.id}>
                  {schema.plural_name || schema.name}
                </option>
              ))}
            </select>
            {schemaIsMissing && (
              <span className={formStyles.fieldError} role="alert">
                {AGENT_OUTCOME_COPY.missingSchema}
              </span>
            )}
            {schemaIsUnavailable && (
              <span className={formStyles.fieldError} role="alert">
                {AGENT_OUTCOME_COPY.unavailableSchema}
              </span>
            )}
          </Field>

          {schemasAreLoading && (
            <p className={formStyles.helpText}>{AGENT_OUTCOME_COPY.loadingSchemas}</p>
          )}

          {schemasFailedToLoad && (
            <p className={formStyles.fieldError} role="alert">
              {AGENT_OUTCOME_COPY.schemaLoadFailure}{' '}
              <button
                type="button"
                className={formStyles.linkButton}
                onClick={() => void schemaDefinitionsQuery.refetch()}>
                {AGENT_OUTCOME_COPY.retrySchemaLoad}
              </button>
              .
            </p>
          )}

          {eligibleSchemas !== undefined && eligibleSchemas.length === 0 && (
            <p className={formStyles.helpText}>{AGENT_OUTCOME_COPY.noActiveSchemas}</p>
          )}
        </>
      )}
    </Section>
  );
};
